import React, { useEffect, useMemo, useState } from "react";
import styles from "./ActivityMapping.module.css";
import { Modal } from "react-bootstrap";
import ActivityDropdown from "./ActivityDropdown";
import {
  getInternalK1ActivitiesData,
  saveInternalK1ActivitiesMappings,
} from "./ActivityMapping.service";
import { ThreeCircles } from "react-loader-spinner";
import { CustomButton } from "reusable-components/CustomButtonComponent/button";

const ActivityMapping: React.FC<any> = ({
  show,
  onHide,
  refresh,
  returnid,
  clientmasterid,
  parentreturnid,
  parentName,
  clientname,
  documentid,
  sourceidentifier,
  setinternalk1activities,
  internalk1activities,
  activitymappings
}) => {

  const [loadActivityMappings, setloadActivityMappings] = useState<boolean>(false);
  const [sliderEnabled, setSliderEnabled] = useState<boolean>(true);
  const [childActivties, setchildActivties] = useState<any[]>([]);
  const [parentActivities, setParentActivities] = useState<any[]>([]);
  const [parentActivitiesForPayload, setParentActivitiesForPayload] = useState<any[]>([]);
  const [selectedChildren, setselectedChildren] = useState<{ [key: string]: { label: string, value: string } }>({});
  const [childOptions, setChildOptions] = useState<any[]>([]);
  const [saveActivityMappingsFlag, setSaveActivityMappingsFlag] = useState<boolean>(false);
  const [initialMappingSignature, setInitialMappingSignature] = useState<string | null>(null);

  const handleClose = () => { onHide(); };

  const onChildActivityChange = (option: any, selectionKey: string) => {
    setselectedChildren(prev => ({ ...prev, [selectionKey]: option }));
  };

  const getActivityNumber = (activity: any, fallback: any = "") =>
    activity?.ActivityNumber ??
    activity?.ChildActivityNumber ??
    activity?.ParentActivityNumber ??
    fallback;

  const getActivityDescription = (activity: any) =>
    activity?.ActivityDescription ??
    activity?.ChildActivityDescription ??
    activity?.ParentActivityDescription ??
    "";

  const formatActivityLabel = (number: any, description: any) => {
    const activityNumber = String(number ?? "").trim();
    const activityDescription = String(description ?? "").trim();
    if (activityNumber && activityDescription) return `${activityNumber}: ${activityDescription}`;
    return activityDescription || activityNumber;
  };

  const getDescriptionFromOptionLabel = (label: string) => {
    const parts = String(label ?? "").split(":");
    return parts.length > 1 ? parts.slice(1).join(":").trim() : String(label ?? "").trim();
  };

  const isTemporaryActivityValue = (value: any) => String(value ?? "").includes("_temp");

  const findOptionForActivity = (
    options: any[],
    activityNumber: any,
    activityDescription: any,
  ) => {
    const value = String(activityNumber ?? "").trim();
    const description = String(activityDescription ?? "").trim();
    return options.find((option: any) => (
      option.value === value &&
      (!description || option.label === formatActivityLabel(value, description))
    )) ??
      options.find((option: any) => option.value === value && value !== "") ??
      options.find((option: any) => getDescriptionFromOptionLabel(option.label) === description && description !== "");
  };

  const getParentSelectionKey = (parent: any, idx: number) => String(parent?.ActivityNumber ?? idx);

  const getActivitySortValue = (activity: any, fallback: number) => {
    const value = String(getActivityNumber(activity, fallback)).trim();
    const numericValue = Number.parseInt(value, 10);
    return Number.isNaN(numericValue) ? Number.MAX_SAFE_INTEGER : numericValue;
  };

  const sortActivitiesByNumber = (activities: any[]) =>
    [...activities].sort((a, b) => (
      getActivitySortValue(a, Number.MAX_SAFE_INTEGER) -
      getActivitySortValue(b, Number.MAX_SAFE_INTEGER)
    ));

  const buildMappings = (
    activities: any[],
    selectedMap: { [key: string]: { label: string, value: string } },
  ) => activities
    .map((parent, idx) => {
      const sel = selectedMap[getParentSelectionKey(parent, idx)];
      if (!sel) return null;
      const isNewActivity = isTemporaryActivityValue(sel.value);
      return {
        ChildActivityNumber: isNewActivity ? "" : String(sel.value),
        ChildActivityDescription: isNewActivity || sel.label === "New Activity"
          ? parent.ActivityDescription
          : getDescriptionFromOptionLabel(sel.label),
        ParentActivityNumber: String(parent.ActivityNumber),
        ParentActivityDescription: (parent.ActivityDescription || "").trim()
      };
    })
    .filter(Boolean);

  const buildMappingSignature = (
    activities: any[],
    selectedMap: { [key: string]: { label: string, value: string } },
  ) => JSON.stringify(buildMappings(activities, selectedMap));

  const hasActivityMappingChanges = useMemo(() => {
    if (initialMappingSignature === null || loadActivityMappings) return false;
    return initialMappingSignature !== buildMappingSignature(
      parentActivitiesForPayload,
      selectedChildren,
    );
  }, [
    initialMappingSignature,
    loadActivityMappings,
    parentActivitiesForPayload,
    selectedChildren,
  ]);

  useEffect(() => {
    getActivitiesDataForInternalK1();
  }, [internalk1activities, documentid]);

  const upsertActivities = (
    list: any[] | undefined,
    update: { DocumentId: number; ParentReturnId?: any; Mappings: any[] },
    { merge = false } = {}
  ) => {
    const arr = Array.isArray(list) ? list : [];
    const i = arr.findIndex(x => Number(x.DocumentId) === Number(update.DocumentId));

    const norm = (m: any) => ({
      ChildActivityNumber: String(m?.ChildActivityNumber),
      ChildActivityDescription: String(m?.ChildActivityDescription ?? "").trim(),
      ParentActivityNumber: String(m?.ParentActivityNumber ?? 0),
      ParentActivityDescription: String(m?.ParentActivityDescription ?? "").trim(),
    });
    const dedupe = (xs: any[]) => {
      const seen = new Set<string>();
      const out: any[] = [];
      for (const r of xs ?? []) {
        const m = norm(r);
        const key = `${m.ChildActivityNumber}:${m.ParentActivityNumber}`;
        if (!seen.has(key)) { seen.add(key); out.push(m); }
      }
      return out;
    };

    const nextMappings = dedupe(update.Mappings);

    if (i === -1) {
      return [...arr, { DocumentId: update.DocumentId, ParentReturnId: update.ParentReturnId, Mappings: nextMappings }];
    }

    const current = arr[i];
    const merged = merge ? dedupe([...(current.Mappings ?? []), ...nextMappings]) : nextMappings;

    const sameLen = (current.Mappings?.length ?? 0) === merged.length;
    const sameMaps = sameLen && current.Mappings?.every((m: any, idx: number) => {
      const n = merged[idx];
      return (
        m.ChildActivityNumber === n.ChildActivityNumber &&
        m.ParentActivityNumber === n.ParentActivityNumber &&
        m.ChildActivityDescription === n.ChildActivityDescription &&
        m.ParentActivityDescription === n.ParentActivityDescription
      );
    });
    const sameParent = current.ParentReturnId === update.ParentReturnId;
    if (sameMaps && sameParent) return arr;

    const copy = [...arr];
    copy[i] = { ...current, ParentReturnId: update.ParentReturnId, Mappings: merged };
    return copy;
  };

  const getActivitiesDataForInternalK1 = async () => {
    try {
      setloadActivityMappings(true);
      const payload = {
        ClientMasterId: clientmasterid,
        DocumentId: documentid,
        ReturnId: returnid,
        ParentReturnId: parentreturnid,
        SourceIdentifier: sourceidentifier,
      };
      const data: any = await getInternalK1ActivitiesData(payload);

      const originalParentActivitiesFromApi = Array.isArray(data?.ParentActivities) ? data.ParentActivities : [];
      const parentActivitiesFromApi = sortActivitiesByNumber(originalParentActivitiesFromApi);
      setParentActivities(parentActivitiesFromApi);
      setParentActivitiesForPayload(originalParentActivitiesFromApi);

      let saved: any = null;
      if (Array.isArray(internalk1activities) && internalk1activities.length) {
        saved = internalk1activities.find((m: any) => m.DocumentId === documentid) ?? null;
      }
      const initialSliderEnabled = true;
      setSliderEnabled(initialSliderEnabled);

      const externalMappings = (typeof activitymappings !== "undefined" && activitymappings[documentid])
        ? activitymappings[documentid]
        : null;

      const apiChildActivities = Array.isArray(data?.ChildActivities) ? data.ChildActivities : [];
      const childActivitiesForOptions = apiChildActivities.length > 0 ? apiChildActivities : (externalMappings ?? []);
      const newOptions = sortActivitiesByNumber(childActivitiesForOptions).reduce((options: any[], child: any, idx: any) => {
        const number = getActivityNumber(child, idx);
        const description = getActivityDescription(child);
        const option = {
          label: formatActivityLabel(number, description),
          value: number ? String(number) : String(idx),
        };
        if (!options.some((item) => item.value === option.value && item.label === option.label)) {
          options.push(option);
        }
        return options;
      }, []);
      setChildOptions(newOptions);
      const childActivitiesFromApi = apiChildActivities.length > 0 ? apiChildActivities : (externalMappings ?? []);

      const fallbackNumberByIndex = (idx: number) =>
        childActivitiesFromApi[idx] ? String(getActivityNumber(childActivitiesFromApi[idx])) : "";
      const fallbackDescByIndex = (idx: number) =>
        childActivitiesFromApi[idx] ? String(getActivityDescription(childActivitiesFromApi[idx])) : "";

      const chosenMappings = Array.isArray(externalMappings) && externalMappings.length > 0
        ? externalMappings
        : Array.isArray(saved?.Mappings) && saved.Mappings.length > 0
        ? saved.Mappings
        : null;

      let normalized: any[] = [];
      if (chosenMappings) {
        normalized = chosenMappings.map((mapItem: any, idx: number) => {
          const activityNumber = String(mapItem.ChildActivityNumber ?? fallbackNumberByIndex(idx) ?? "");
          const activityDesc =
            String(mapItem.ChildActivityDescription ?? "") || fallbackDescByIndex(idx) || "";
          return {
            ActivityNumber: activityNumber,
            ActivityDescription: activityDesc,
            Description: activityDesc,
            ParentActivityNumber: String(mapItem.ParentActivityNumber ?? parentActivitiesFromApi[idx]?.ActivityNumber ?? idx),
          };
        });
      } else {
        normalized = childActivitiesFromApi.map((child: any, idx: any) => ({
          ActivityNumber: String(getActivityNumber(child, idx)),
          ActivityDescription: String(getActivityDescription(child)),
          Description: String(getActivityDescription(child)),
          ParentActivityNumber: String(parentActivitiesFromApi[idx]?.ActivityNumber ?? idx),
        }));
      }

      const normalizedByParentNumber = new Map(
        normalized.map((item: any, idx: number) => [
          String(item.ParentActivityNumber ?? parentActivitiesFromApi[idx]?.ActivityNumber ?? idx),
          item,
        ]),
      );
      const selectedChildrenMap: { [key: string]: { label: string; value: any } } = {};
      parentActivitiesFromApi.forEach((parent: any, idx: number) => {
        const parentNumber = getParentSelectionKey(parent, idx);
        const m = normalizedByParentNumber.get(parentNumber) ?? normalized[idx];
        if (!m) return;
        const val = String(m.ActivityNumber) ?? String(idx);
        const option = findOptionForActivity(newOptions, val, m.ActivityDescription);
        selectedChildrenMap[parentNumber] = {
          label: option ? option.label : formatActivityLabel(val, m.ActivityDescription),
          value: option ? option.value : val,
        };
      });
      setselectedChildren(selectedChildrenMap);
      setInitialMappingSignature(buildMappingSignature(
        originalParentActivitiesFromApi,
        selectedChildrenMap,
      ));

      let finalChildActivities = parentActivitiesFromApi.map((parent: any, idx: number) => {
        const parentNumber = getParentSelectionKey(parent, idx);
        return normalizedByParentNumber.get(parentNumber) ?? normalized[idx];
      }).filter(Boolean);
      const parentCount = parentActivitiesFromApi.length;
      if (parentCount > finalChildActivities.length) {
        const padLen = parentCount - finalChildActivities.length;
        const startIndex = finalChildActivities.length;
        const pads = Array.from({ length: padLen }, (_, i) => {
          const idx = startIndex + i + 1;
          return { ActivityNumber: `${idx}_temp`, Description: `New Activity`, ActivityDescription: `New Activity` };
        });
        finalChildActivities = [...finalChildActivities, ...pads];
      }
      setchildActivties(finalChildActivities);
    } catch (err) {
      console.error("getActivitiesDataForInternalK1 error:", err);
    } finally {
      setloadActivityMappings(false);
    }
  };

  const saveMapping = async () => {
    await generateMappings();
  };

  const getOptionsForIndex = (idx: number) => {
    const newActivityOption = { label: "New Activity", value: `${idx}_temp` };
    return [newActivityOption, ...childOptions];
  };

  const generateMappings = async () => {
    if (!hasActivityMappingChanges || saveActivityMappingsFlag) return;
    setSaveActivityMappingsFlag(true);

    const mappings = buildMappings(parentActivitiesForPayload, selectedChildren);

    let next = upsertActivities(
      internalk1activities,
      { DocumentId: documentid, ParentReturnId: parentreturnid, Mappings: mappings },
      { merge: false }
    );

    try {
      if (sliderEnabled) {
        const payload = {
          ClientMasterId: clientmasterid,
          SelectedReturn: returnid,
          InternalK1Activities: next
        };
        const response = await saveInternalK1ActivitiesMappings(payload);
        if (response?.success && typeof refresh === "function") {
          refresh();
        }
      } else if (!sliderEnabled) {
        next = next.map((item) =>
          item.DocumentId === documentid ? { ...item, IsAggregate: true } : item
        );
      }
      setinternalk1activities(next);
      onHide();
    } finally {
      setSaveActivityMappingsFlag(false);
    }
  };

  return (
    <>
      <div className={styles.modaloverlay} />
      <Modal
        show={show}
        onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        centered
        dialogClassName={styles.activityMappingDialog}
        contentClassName={styles.confirmModal}
      >
        <Modal.Header className={styles.modalHeader}>
          <div className={styles.headerRow}>
            <h5 className={styles.header}>Map Activities</h5>
            <button type="button" className={styles.closeButton} onClick={handleClose} aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <p className={styles.subLabel}>How should activities be shown?</p>
          <div className={styles.modeToggle}>
            <button
              className={`${styles.modeToggleBtn} ${sliderEnabled ? styles.modeToggleActive : ""}`}
              onClick={() => setSliderEnabled(true)}
            >
              <span className={styles.modeTitle}>Separate</span>
              <span className={styles.modeDesc}>Keep activities separate for each partner</span>
            </button>
            <button
              className={`${styles.modeToggleBtn} ${!sliderEnabled ? styles.modeToggleActive : ""}`}
              onClick={() => setSliderEnabled(false)}
            >
              <span className={styles.modeTitle}>Aggregate</span>
              <span className={styles.modeDesc}>Combine all activities into one sheet</span>
            </button>
          </div>
        </Modal.Header>

        <Modal.Body className={styles.modalBody}>
          {loadActivityMappings ? (
            <div className={styles.loadingOverlay}>
              <ThreeCircles
                height="50"
                width="50"
                visible={true}
                ariaLabel="three-circles-rotating"
                outerCircleColor="var(--theme1_tertiary)"
                innerCircleColor="var(--theme1_secondary)"
                middleCircleColor="var(--theme1_primary)"
              />
              <span className={styles.loadingText}>Loading</span>
            </div>
          ) : sliderEnabled ? (
            parentActivities.length === 0 ? (
              <div className={styles.emptyState}>
                <p>{parentName} does not have activity wise data for {clientname}. Please use aggregate transfer.</p>
              </div>
            ) : (
              <div className={styles.mappingContainer}>
                <div className={styles.columnHeaders}>
                  <span className={styles.columnHeader}>{parentName}</span>
                  <span />
                  <span className={styles.columnHeader}>{clientname}</span>
                </div>
                <div className={styles.scrollableMappingSection}>
                  {Array.from({ length: parentActivities.length }).map((_, idx) => (
                    <div key={getParentSelectionKey(parentActivities[idx], idx)} className={styles.mappingRow}>
                      <div className={styles.parentCell}>
                        {parentActivities[idx] && (
                          <span className={styles.activityLabel}>
                            {formatActivityLabel(
                              parentActivities[idx].ActivityNumber ?? idx + 1,
                              parentActivities[idx].ActivityDescription,
                            )}
                          </span>
                        )}
                      </div>
                      <div className={styles.arrowCell}>
                        <span className={styles.arrow}>&rarr;</span>
                      </div>
                      <div className={styles.childCell}>
                        {childActivties[idx] && (
                          <ActivityDropdown
                            value={selectedChildren[getParentSelectionKey(parentActivities[idx], idx)] || null}
                            options={getOptionsForIndex(idx)}
                            onChange={(option) => onChildActivityChange(
                              option,
                              getParentSelectionKey(parentActivities[idx], idx),
                            )}
                            placeholder="Select child activity"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ) : (
            <div className={styles.aggregateState}>
              <p>You don't have to map for aggregate data view</p>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className={styles.modalFooter}>
          <CustomButton
            variant="modalSecondary"
            type="button"
            onclick={handleClose}
            content="Close"
          />
          <CustomButton
            variant="modalPrimary"
            type="button"
            onclick={() => saveMapping()}
            content={saveActivityMappingsFlag ? "Saving" : "Save"}
            progress={saveActivityMappingsFlag}
            disable={saveActivityMappingsFlag || !hasActivityMappingChanges}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ActivityMapping;
